import styled from 'styled-components';

/**
 * The crop frame: a white rectangle whose enormous spread shadow darkens
 * everything outside it. One element instead of four shade panels - the
 * CanvasBox's `overflow: hidden` clips the shadow to the picture.
 */
export const CropRectangle = styled.div`
  position: absolute;
  border: 2px solid ${({ theme }) => theme.white};
  box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.55);
  cursor: move;
  touch-action: none;

  /*
   * Rule-of-thirds guides. Two pseudo-elements inset by a third and wearing
   * borders on their facing edges - four lines out of two elements, and they
   * follow the frame as it is dragged because they are sized in percentages
   * of it rather than positioned.
   *
   * Half-opacity white, the same as every camera's crop grid: it has to be
   * readable over a photo without competing with the frame that bounds it.
   */
  ::before,
  ::after {
    content: '';
    position: absolute;
  }

  ::before {
    top: 0;
    bottom: 0;
    left: 33.333%;
    right: 33.333%;
    border-left: 1px solid rgba(255, 255, 255, 0.5);
    border-right: 1px solid rgba(255, 255, 255, 0.5);
  }

  ::after {
    left: 0;
    right: 0;
    top: 33.333%;
    bottom: 33.333%;
    border-top: 1px solid rgba(255, 255, 255, 0.5);
    border-bottom: 1px solid rgba(255, 255, 255, 0.5);
  }
`;

type Corner = 'nw' | 'ne' | 'sw' | 'se';

const CORNER_EDGES: Record<Corner, string> = {
  nw: 'top: 0; left: 0; border-top: 4px solid; border-left: 4px solid;',
  ne: 'top: 0; right: 0; border-top: 4px solid; border-right: 4px solid;',
  sw: 'bottom: 0; left: 0; border-bottom: 4px solid; border-left: 4px solid;',
  se: 'bottom: 0; right: 0; border-bottom: 4px solid; border-right: 4px solid;',
};

/**
 * A corner handle: a 24px invisible hit area wearing a white L in its corner,
 * drawn 4px thick so it reads over any photo.
 *
 * Flush INSIDE the frame, not straddling it. Straddling looks better on an
 * inset crop, but the crop starts covering the whole picture - and CanvasBox
 * clips to the picture's edges, so every handle was cut in half at exactly the
 * moment the user first needs to find them.
 *
 * The drop shadow is what keeps a white L visible on a white subject; the
 * frame line itself is thin enough to read as an edge either way, but these
 * have to be found by eye.
 */
export const CropHandle = styled.div<{ $corner: Corner }>`
  position: absolute;
  width: 24px;
  height: 24px;
  touch-action: none;
  filter: drop-shadow(0 0 2px rgba(0, 0, 0, 0.6));
  ${({ $corner }) => CORNER_EDGES[$corner]}
  /* AFTER the corner edges, not before. Those are border-top / border-left
     SHORTHANDS, and a shorthand resets the colour it does not mention back to
     currentColor - so a border-color declared above them is overridden and the
     handles come out in the inherited text colour. */
  border-color: ${({ theme }) => theme.white};
  cursor: ${({ $corner }) =>
    $corner === 'nw' || $corner === 'se' ? 'nwse-resize' : 'nesw-resize'};
`;
